import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Layout } from '../components/Layout';
import { FileText, Plus, Trash2, Send, Download, X, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEE_PRESETS = [
  'Frais de deblocage de virement',
  'Frais de transfert international',
  'Frais d\'activation de compte',
  'Frais de conformite',
  'Frais de traitement',
  'Frais de regularisation',
  'Frais administratifs',
  'Commission bancaire',
];

function CreateInvoiceModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ client_name: '', client_email: '', currency: 'EUR', due_date: '', notes: '', language: 'fr', sender_bank: '' });
  const [items, setItems] = useState([{ description: '', amount: '' }]);
  const [saving, setSaving] = useState(false);
  const [senderBanks, setSenderBanks] = useState([]);
  const [actionableTransfers, setActionableTransfers] = useState([]);
  const [selectedTransferIds, setSelectedTransferIds] = useState([]);

  useEffect(() => {
    axios.get(`${API}/sender-banks`).then(r => setSenderBanks(r.data)).catch(() => {});
    axios.get(`${API}/transfers/actionable`).then(r => setActionableTransfers(r.data)).catch(() => {});
  }, []);

  const addItem = () => setItems([...items, { description: '', amount: '' }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: val };
    setItems(updated);
  };

  const toggleTransfer = (id) => {
    setSelectedTransferIds(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };

  const total = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.client_email || items.some(i => !i.description || !i.amount)) {
      toast.error('Remplissez tous les champs');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, items: items.map(i => ({ description: i.description, amount: parseFloat(i.amount) })), transfer_ids: selectedTransferIds.length > 0 ? selectedTransferIds : undefined };
      await axios.post(`${API}/invoices`, payload);
      toast.success('Facture creee');
      onCreated();
      onClose();
    } catch { toast.error('Erreur creation'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-invoice-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Nouvelle facture</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Nom du client</label>
              <input data-testid="invoice-client-name" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0066FF]" value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Email du client</label>
              <input data-testid="invoice-client-email" type="email" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0066FF]" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Devise</label>
              <select data-testid="invoice-currency" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                {['EUR', 'USD', 'GBP', 'CHF', 'XPF', 'XOF', 'XAF', 'CAD'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Date d'echeance</label>
              <input data-testid="invoice-due-date" type="date" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0066FF]" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Langue</label>
              <select data-testid="invoice-language" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none" value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
                <option value="fr">Francais</option>
                <option value="en">English</option>
                <option value="es">Espanol</option>
                <option value="pt">Portugues</option>
                <option value="it">Italiano</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Banque emettrice</label>
              <select data-testid="invoice-sender-bank" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none" value={form.sender_bank} onChange={e => setForm({ ...form, sender_bank: e.target.value })}>
                <option value="">-- Aucune --</option>
                {senderBanks.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
              </select>
            </div>
          </div>

          {/* Linked Transfers */}
          {actionableTransfers.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2">Virements associes (en attente / rejetes)</label>
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                {actionableTransfers.map(t => (
                  <label key={t.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm" data-testid={`link-transfer-${t.id}`}>
                    <input type="checkbox" checked={selectedTransferIds.includes(t.id)} onChange={() => toggleTransfer(t.id)} className="rounded border-slate-300" />
                    <span className="flex-1 text-slate-700 truncate">{t.beneficiary_name}</span>
                    <span className="text-xs text-slate-500">{t.amount?.toLocaleString('fr-FR')} {t.currency}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                      {t.status === 'pending' ? 'Attente' : 'Rejete'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-500">Lignes de facturation</label>
              <button type="button" onClick={addItem} className="text-xs text-[#0066FF] hover:text-[#0052CC] font-medium">+ Ajouter une ligne</button>
            </div>
            {/* Presets */}
            <div className="flex flex-wrap gap-1 mb-3">
              {FEE_PRESETS.map(p => (
                <button
                  key={p}
                  type="button"
                  className="text-[10px] px-2 py-1 bg-slate-100 text-slate-600 rounded-md hover:bg-[#0066FF]/5 hover:text-[#0052CC] transition-colors"
                  onClick={() => {
                    const emptyIdx = items.findIndex(i => !i.description);
                    if (emptyIdx >= 0) updateItem(emptyIdx, 'description', p);
                    else setItems([...items, { description: p, amount: '' }]);
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {items.map((item, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`invoice-item-${i}`}>
                  <input
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0066FF]"
                    placeholder="Description"
                    value={item.description}
                    onChange={e => updateItem(i, 'description', e.target.value)}
                    required
                  />
                  <input
                    className="w-28 px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0066FF] text-right"
                    placeholder="Montant"
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.amount}
                    onChange={e => updateItem(i, 'amount', e.target.value)}
                    required
                  />
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">TOTAL</span>
            <span data-testid="invoice-total" className="text-lg font-bold text-[#0066FF]">{total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {form.currency}</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Notes (optionnel)</label>
            <textarea data-testid="invoice-notes" rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0066FF] resize-none" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>

          <Button data-testid="create-invoice-btn" type="submit" className="w-full" disabled={saving}>
            <FileText className="w-4 h-4 mr-1" /> {saving ? 'Creation...' : 'Creer la facture'}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);

  const fetchInvoices = () => {
    axios.get(`${API}/invoices`)
      .then(r => { setInvoices(r.data); setLoading(false); })
      .catch(() => { toast.error('Erreur chargement'); setLoading(false); });
  };

  useEffect(() => { fetchInvoices(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success('Facture supprimee');
      fetchInvoices();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleDownload = async (inv) => {
    try {
      const r = await axios.get(`${API}/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `WorldPay_Facture_${inv.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erreur telechargement'); }
  };

  const handlePreview = (inv) => setPreviewInvoice(inv);

  const handleSend = async (inv) => {
    setSendingId(inv.id);
    try {
      await axios.post(`${API}/invoices/${inv.id}/send`);
      toast.success(`Facture envoyee a ${inv.client_email}`);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi');
    }
    setSendingId(null);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return d; }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="invoices-title">
              <FileText className="w-6 h-6 text-[#0066FF]" /> Factures
            </h1>
            <p className="text-sm text-slate-500 mt-1">Generez et envoyez des factures aux clients</p>
          </div>
          <Button data-testid="new-invoice-btn" onClick={() => setShowCreate(true)} className="w-full sm:w-auto bg-[#0066FF] hover:bg-[#0052CC] rounded-lg">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle facture
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#0066FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="no-invoices">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Aucune facture. Cliquez sur "Nouvelle facture" pour commencer.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="invoices-list">
            {invoices.map(inv => (
              <div key={inv.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow" data-testid={`invoice-${inv.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 bg-[#0066FF]/5 rounded-lg flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-[#0066FF]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-500 truncate">{inv.client_name} - {formatDate(inv.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-14 sm:ml-0">
                    <span className="text-lg font-bold text-slate-900">{inv.total?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {inv.currency}</span>
                    <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${inv.status === 'sent' ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                      {inv.status === 'sent' ? 'ENVOYEE' : 'BROUILLON'}
                    </span>
                  </div>
                </div>
                {/* Items summary */}
                <div className="mt-3 ml-14">
                  <div className="flex flex-wrap gap-1">
                    {inv.items?.map((item, i) => (
                      <span key={i} className="text-[11px] bg-slate-50 text-slate-600 px-2 py-0.5 rounded">{item.description}: {item.amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {inv.currency}</span>
                    ))}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex flex-wrap gap-2 mt-3 ml-0 sm:ml-14">
                  <Button variant="outline" size="sm" className="flex-1 sm:flex-none rounded-lg" data-testid={`preview-${inv.id}`} onClick={() => handlePreview(inv)}>
                    <Eye className="w-3 h-3 mr-1" /> Apercu
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 sm:flex-none rounded-lg" data-testid={`download-${inv.id}`} onClick={() => handleDownload(inv)}>
                    <Download className="w-3 h-3 mr-1" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 sm:flex-none rounded-lg" data-testid={`send-${inv.id}`} onClick={() => handleSend(inv)} disabled={sendingId === inv.id}>
                    <Send className="w-3 h-3 mr-1" /> {sendingId === inv.id ? 'Envoi...' : 'Envoyer'}
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 sm:flex-none rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-${inv.id}`} onClick={() => handleDelete(inv.id)}>
                    <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreate && <CreateInvoiceModal onClose={() => setShowCreate(false)} onCreated={fetchInvoices} />}

        {/* Invoice Preview Modal */}
        {previewInvoice && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreviewInvoice(null)}>
            <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="invoice-preview-modal">
              <div className="bg-slate-900 p-5 rounded-t-xl flex items-center justify-between">
                <div>
                  <h2 className="text-white font-bold text-lg">WORLDPAY</h2>
                  <p className="text-slate-400 text-xs">Service des Operations Bancaires</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">FACTURE</p>
                  <p className="text-slate-400 text-xs">{previewInvoice.invoice_number}</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Date</span>
                  <span>{formatDate(previewInvoice.created_at)}</span>
                </div>
                {previewInvoice.due_date && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Echeance</span>
                    <span>{previewInvoice.due_date}</span>
                  </div>
                )}
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-400 font-semibold tracking-wider">FACTURE A</p>
                  <p className="font-bold text-slate-900">{previewInvoice.client_name}</p>
                  <p className="text-sm text-slate-500">{previewInvoice.client_email}</p>
                </div>
                <div>
                  <div className="bg-slate-900 text-white text-xs font-semibold px-3 py-2 rounded-t flex justify-between">
                    <span>DESCRIPTION</span><span>MONTANT</span>
                  </div>
                  <div className="border border-slate-200 border-t-0 rounded-b">
                    {previewInvoice.items?.map((item, i) => (
                      <div key={i} className="flex justify-between px-3 py-2 text-sm border-b border-slate-100 last:border-0">
                        <span className="text-slate-700">{item.description}</span>
                        <span className="font-medium">{item.amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {previewInvoice.currency}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 flex justify-between items-center">
                  <span className="font-bold text-slate-900">TOTAL</span>
                  <span className="text-xl font-bold text-blue-600">{previewInvoice.total?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {previewInvoice.currency}</span>
                </div>
                {previewInvoice.notes && (
                  <div className="text-xs text-slate-500">
                    <p className="font-semibold text-slate-400 mb-1">NOTES</p>
                    <p>{previewInvoice.notes}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2 p-4 border-t border-slate-200">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => handleDownload(previewInvoice)}>
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => { handleSend(previewInvoice); setPreviewInvoice(null); }}>
                  <Send className="w-3 h-3 mr-1" /> Envoyer
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPreviewInvoice(null)}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
